import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ADMIN_HOME } from "@/constants/paths/admin"
import { formatBreadcrumbSegment } from "@/config/breadcrumbs"
import { useActiveBusinessId } from "@/hooks/use-business"
import { useGetConversationsQuery } from "@/queries/chat"

export interface BreadcrumbItem {
  label: string
  path?: string
}

const MAX_VISIBLE_CRUMBS = 4
const MONGO_ID_RE = /^[a-f0-9]{24}$/i
const MESSAGE_THREAD_RE = /^\/messages\/([a-f0-9]{24})(?:\/|$)/i

export function useBreadcrumbs(scope: "business" | "admin") {
  const location = useLocation()
  const activeBusinessId = useActiveBusinessId()
  const { t } = useTranslation("common")

  const conversationId = useMemo(() => {
    if (scope !== "business") return null
    return location.pathname.match(MESSAGE_THREAD_RE)?.[1] ?? null
  }, [location.pathname, scope])

  const { data: conversationsData } = useGetConversationsQuery(
    activeBusinessId ?? undefined,
    { enabled: !!conversationId && !!activeBusinessId },
  )

  const conversationCustomerName = useMemo(() => {
    if (!conversationId) return null
    const match = conversationsData?.data?.find(
      (item) => item._id === conversationId,
    )
    return match?.customer?.name?.trim() || null
  }, [conversationId, conversationsData?.data])

  return useMemo(() => {
    const root: BreadcrumbItem =
      scope === "admin"
        ? { label: t("adminNav.adminHome"), path: ADMIN_HOME }
        : { label: t("nav.dashboard"), path: "/dashboard" }

    const rawSegments =
      scope === "admin"
        ? location.pathname.replace(/^\/admin\/?/, "").split("/").filter(Boolean)
        : (() => {
            const segments = location.pathname.split("/").filter(Boolean)
            return segments[0] === "dashboard" ? segments.slice(1) : segments
          })()

    const segments: BreadcrumbItem[] = rawSegments.map((segment, index) => {
      const path =
        scope === "admin"
          ? `${ADMIN_HOME}/${rawSegments.slice(0, index + 1).join("/")}`
          : `/${rawSegments.slice(0, index + 1).join("/")}`

      const parent = index > 0 ? rawSegments[index - 1] : undefined
      const label =
        parent === "messages" &&
        MONGO_ID_RE.test(segment) &&
        conversationCustomerName
          ? conversationCustomerName
          : formatBreadcrumbSegment(segment)

      return {
        label,
        path: index < rawSegments.length - 1 ? path : undefined,
      }
    })

    const items = [root, ...segments]

    if (items.length <= MAX_VISIBLE_CRUMBS) {
      return { items, collapsed: false as const }
    }

    const tail = items.slice(-2)
    const head = items[0]
    return {
      items: [head, { label: "…" }, ...tail],
      collapsed: true as const,
    }
  }, [conversationCustomerName, location.pathname, scope, t])
}
